import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2, Edit2, Users, Filter, Copy, Loader2, Tag, MapPin, Building2, ChevronRight, Landmark, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

// ─── Tipos ────────────────────────────────────────────────────────────────────

type ConditionField =
  | "tag"
  | "contact_type"
  | "state"
  | "city"
  | "organ"
  | "sector"
  | "job_title"
  | "opted_out";

type Condition = {
  id: string;
  field: ConditionField;
  operator: "is" | "is_not" | "contains" | "not_contains";
  value: string;
};

type SegmentFormData = {
  name: string;
  description: string;
  conditions: Condition[];
  matchType: "all" | "any";
};

const FIELD_LABELS: Record<ConditionField, string> = {
  tag: "Tag",
  contact_type: "Tipo de contato",
  state: "Estado (UF)",
  city: "Cidade",
  organ: "Órgão / Empresa",
  sector: "Setor / Secretaria",
  job_title: "Cargo",
  opted_out: "Status de opt-out",
};

const FIELD_ICONS: Record<ConditionField, any> = {
  tag: Tag,
  contact_type: Landmark,
  state: MapPin,
  city: MapPin,
  organ: Building2,
  sector: ChevronRight,
  job_title: Briefcase,
  opted_out: Users,
};

const OPERATORS_FOR_FIELD: Record<ConditionField, { value: string; label: string }[]> = {
  tag: [
    { value: "is", label: "possui a tag" },
    { value: "is_not", label: "não possui a tag" },
  ],
  contact_type: [
    { value: "is", label: "é" },
    { value: "is_not", label: "não é" },
  ],
  state: [
    { value: "is", label: "é" },
    { value: "is_not", label: "não é" },
  ],
  city: [
    { value: "contains", label: "contém" },
    { value: "is", label: "é exatamente" },
    { value: "is_not", label: "não é" },
    { value: "not_contains", label: "não contém" },
  ],
  organ: [
    { value: "contains", label: "contém" },
    { value: "is", label: "é exatamente" },
    { value: "is_not", label: "não é" },
    { value: "not_contains", label: "não contém" },
  ],
  sector: [
    { value: "contains", label: "contém" },
    { value: "is", label: "é exatamente" },
    { value: "is_not", label: "não é" },
    { value: "not_contains", label: "não contém" },
  ],
  job_title: [
    { value: "contains", label: "contém" },
    { value: "is", label: "é exatamente" },
    { value: "is_not", label: "não é" },
    { value: "not_contains", label: "não contém" },
  ],
  opted_out: [
    { value: "is", label: "é" },
  ],
};

const BRAZIL_STATES = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS",
  "MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"
];

const CONTACT_TYPES = [
  { value: "public_admin", label: "Administração Pública" },
  { value: "empresa", label: "Empresa" },
  { value: "cliente_final", label: "Cliente Final" },
];

// ─── Componente de condição individual ───────────────────────────────────────

function ConditionRow({
  condition,
  tags,
  onChange,
  onRemove,
  canRemove,
}: {
  condition: Condition;
  tags: any[];
  onChange: (updates: Partial<Condition>) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const operators = OPERATORS_FOR_FIELD[condition.field] ?? [{ value: "is", label: "é" }];

  const renderValueInput = () => {
    switch (condition.field) {
      case "tag":
        return (
          <Select value={condition.value || undefined} onValueChange={(v) => onChange({ value: v })}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Selecionar tag..." />
            </SelectTrigger>
            <SelectContent>
              {tags.map((tag: any) => (
                <SelectItem key={tag.id} value={tag.id.toString()}>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tag.color }} />
                    {tag.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "contact_type":
        return (
          <Select value={condition.value || undefined} onValueChange={(v) => onChange({ value: v })}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Selecionar tipo..." />
            </SelectTrigger>
            <SelectContent>
              {CONTACT_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "state":
        return (
          <Select value={condition.value || undefined} onValueChange={(v) => onChange({ value: v })}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Selecionar estado..." />
            </SelectTrigger>
            <SelectContent>
              {BRAZIL_STATES.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "opted_out":
        return (
          <Select value={condition.value || undefined} onValueChange={(v) => onChange({ value: v })}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Selecionar status..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Opt-out (inativo)</SelectItem>
              <SelectItem value="false">Ativo</SelectItem>
            </SelectContent>
          </Select>
        );

      default:
        return (
          <Input
            value={condition.value}
            onChange={(e) => onChange({ value: e.target.value })}
            placeholder={
              condition.field === "city" ? "Ex: São Paulo" :
              condition.field === "organ" ? "Ex: Prefeitura de Campinas" :
              condition.field === "sector" ? "Ex: Secretaria de Administração" :
              condition.field === "job_title" ? "Ex: Pregoeiro" :
              "Digite o valor..."
            }
            className="h-8 text-sm"
          />
        );
    }
  };

  return (
    <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-end p-3 bg-muted/40 rounded-lg border border-border/50">
      {/* Campo */}
      <div>
        <Label className="text-xs text-muted-foreground">Campo</Label>
        <Select
          value={condition.field}
          onValueChange={(value: ConditionField) => onChange({ field: value, value: "", operator: (OPERATORS_FOR_FIELD[value] ?? [])[0]?.value as any ?? "is" })}
        >
          <SelectTrigger className="h-8 text-sm mt-0.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Geral</div>
            <SelectItem value="tag">Tag</SelectItem>
            <SelectItem value="contact_type">Tipo de contato</SelectItem>
            <SelectItem value="opted_out">Status (ativo/inativo)</SelectItem>
            <Separator className="my-1" />
            <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Localização</div>
            <SelectItem value="state">Estado (UF)</SelectItem>
            <SelectItem value="city">Cidade</SelectItem>
            <Separator className="my-1" />
            <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Adm. Pública / Empresa</div>
            <SelectItem value="organ">Órgão / Empresa</SelectItem>
            <SelectItem value="sector">Setor / Secretaria</SelectItem>
            <SelectItem value="job_title">Cargo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Operador */}
      <div>
        <Label className="text-xs text-muted-foreground">Operador</Label>
        <Select
          value={condition.operator}
          onValueChange={(v: any) => onChange({ operator: v })}
        >
          <SelectTrigger className="h-8 text-sm mt-0.5 min-w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {operators.map(op => (
              <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Valor */}
      <div>
        <Label className="text-xs text-muted-foreground">Valor</Label>
        <div className="mt-0.5">
          {renderValueInput()}
        </div>
      </div>

      {/* Remover */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
        disabled={!canRemove}
        aria-label="Remover"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

// ─── Resumo de condição (para listagem) ──────────────────────────────────────

function ConditionSummary({ condition, tags }: { condition: Condition; tags: any[] }) {
  const FieldIcon = FIELD_ICONS[condition.field] ?? Filter;
  const operatorLabel = OPERATORS_FOR_FIELD[condition.field]?.find(o => o.value === condition.operator)?.label ?? condition.operator;

  let valueLabel = condition.value;
  if (condition.field === "tag") {
    const tag = tags.find((t: any) => t.id.toString() === condition.value);
    if (tag) {
      return (
        <span className="flex items-center gap-1 text-xs">
          <FieldIcon className="w-3 h-3" />
          <span className="text-muted-foreground">{operatorLabel}</span>
          <Badge className="text-xs px-1.5 py-0" style={{ backgroundColor: tag.color, color: "white" }}>{tag.name}</Badge>
        </span>
      );
    }
  }
  if (condition.field === "contact_type") {
    valueLabel = CONTACT_TYPES.find(t => t.value === condition.value)?.label ?? condition.value;
  }
  if (condition.field === "opted_out") {
    valueLabel = condition.value === "true" ? "Opt-out" : "Ativo";
  }

  return (
    <span className="flex items-center gap-1 text-xs">
      <FieldIcon className="w-3 h-3 text-muted-foreground" />
      <span className="font-medium">{FIELD_LABELS[condition.field]}</span>
      <span className="text-muted-foreground">{operatorLabel}</span>
      <Badge variant="secondary" className="text-xs px-1.5 py-0">{valueLabel}</Badge>
    </span>
  );
}

// ─── Página Principal ─────────────────────────────────────────────────────────

export default function Segments() {
  const utils = trpc.useUtils();
  const { data: segments = [], isLoading } = trpc.segments.list.useQuery();
  const { data: tags = [] } = trpc.tags.list.useQuery();

  useContextoDeLista({
    titulo: "Segmentos de contatos",
    itens: (segments ?? []).map((sg: any) => ({ id: sg.id, nome: sg.name, condicoes: (sg.conditions ?? []).length, tipo: sg.matchType })),
  });


  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<SegmentFormData>({
    name: "",
    description: "",
    conditions: [{ id: "1", field: "contact_type", operator: "is", value: "" }],
    matchType: "all",
  });
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      conditions: [{ id: "1", field: "contact_type", operator: "is", value: "" }],
      matchType: "all",
    });
    setPreviewCount(null);
    setEditingId(null);
  };

  const createMutation = trpc.segments.create.useMutation({
    onSuccess: () => {
      utils.segments.list.invalidate();
      setIsOpen(false);
      resetForm();
      toast.success("Segmento criado com sucesso!");
    },
    onError: (error: any) => toast.error(toUserFriendlyError(error, "Erro ao criar segmento")),
  });

  const updateMutation = trpc.segments.update.useMutation({
    onSuccess: () => {
      utils.segments.list.invalidate();
      setIsOpen(false);
      resetForm();
      toast.success("Segmento atualizado com sucesso!");
    },
    onError: (error: any) => toast.error(toUserFriendlyError(error, "Erro ao atualizar segmento")),
  });

  const deleteMutation = trpc.segments.delete.useMutation({
    onSuccess: () => { utils.segments.list.invalidate(); toast.success("Segmento removido!"); },
    onError: (error: any) => toast.error(toUserFriendlyError(error, "Erro ao remover segmento")),
  });

  const handleAddCondition = () => {
    const newId = String(Date.now());
    setFormData(f => ({
      ...f,
      conditions: [...f.conditions, { id: newId, field: "tag", operator: "is", value: "" }],
    }));
  };

  const handleUpdateCondition = (id: string, updates: Partial<Condition>) => {
    setFormData(f => ({
      ...f,
      conditions: f.conditions.map(c => c.id === id ? { ...c, ...updates } : c),
    }));
  };

  const handleRemoveCondition = (id: string) => {
    if (formData.conditions.length === 1) { toast.error("Deve haver pelo menos uma condição"); return; }
    setFormData(f => ({ ...f, conditions: f.conditions.filter(c => c.id !== id) }));
  };

  const handleSave = () => {
    if (!formData.name.trim()) { toast.error("Digite um nome para o segmento"); return; }
    if (formData.conditions.some(c => !c.value)) { toast.error("Preencha o valor de todas as condições"); return; }

    const payload = {
      name: formData.name,
      description: formData.description,
      conditions: formData.conditions as any,
      matchType: formData.matchType,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handlePreview = async () => {
    if (formData.conditions.some(c => !c.value)) { toast.error("Preencha todas as condições antes de visualizar"); return; }
    setIsPreviewLoading(true);
    try {
      const result = await utils.client.segments.preview.query({
        conditions: formData.conditions as any,
        matchType: formData.matchType,
      });
      setPreviewCount(result.count);
      toast.success(`${result.count} contato${result.count !== 1 ? "s" : ""} encontrado${result.count !== 1 ? "s" : ""}`);
    } catch {
      toast.error("Erro ao calcular preview do segmento");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const openCreate = () => {
    resetForm();
    setIsOpen(true);
  };

  const openEdit = (segment: any) => {
    setEditingId(segment.id);
    setFormData({
      name: segment.name,
      description: segment.description ?? "",
      conditions: segment.conditions ?? [],
      matchType: segment.matchType ?? "all",
    });
    setPreviewCount(segment.contactCount ?? null);
    setIsOpen(true);
  };

  const handleDuplicate = (segment: any) => {
    setFormData({
      name: `${segment.name} (Cópia)`,
      description: segment.description ?? "",
      conditions: segment.conditions ?? [],
      matchType: segment.matchType ?? "all",
    });
    setEditingId(null);
    setIsOpen(true);
  };

  return (
    <PermissionGuard permission="contacts:manage_segments">
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Segmentos</h1>
            <p className="text-muted-foreground mt-1">
              Agrupe contatos por tipo, localização, órgão, setor ou tags para campanhas direcionadas
            </p>
          </div>
          <Button onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" /> Novo Segmento
          </Button>
        </div>

        {/* Dica de uso */}
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3 text-sm text-blue-900">
              <Filter className="w-4 h-4 mt-0.5 text-blue-600 shrink-0" />
              <div>
                <p className="font-semibold mb-1">Como usar segmentos</p>
                <p className="text-xs text-blue-700">
                  Crie segmentos para filtrar contatos e usá-los em campanhas de WhatsApp ou e-mail.
                  Combine filtros como <strong>Estado + Tipo de órgão + Setor</strong> para atingir exatamente quem você quer.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lista de Segmentos */}
        <Card>
          <CardHeader>
            <CardTitle>Segmentos Criados</CardTitle>
            <CardDescription>{segments.length} segmentos</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Carregando segmentos...
              </div>
            ) : segments.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p className="font-medium">Nenhum segmento criado</p>
                <p className="text-sm mt-1">Crie segmentos para agrupar contatos e disparar campanhas direcionadas</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={openCreate}>
                  <Plus className="w-4 h-4 mr-2" /> Criar primeiro segmento
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {segments.map((segment: any) => (
                  <div key={segment.id} className="p-4 border rounded-lg hover:bg-muted/20 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold truncate">{segment.name}</h3>
                          <Badge variant="outline" className="text-xs shrink-0">
                            {segment.matchType === "all" ? "E" : "OU"}
                          </Badge>
                        </div>
                        {segment.description && (
                          <p className="text-sm text-muted-foreground mb-2">{segment.description}</p>
                        )}
                        {/* Condições resumidas */}
                        <div className="flex flex-wrap gap-2 mb-2">
                          {(segment.conditions ?? []).map((c: Condition, i: number) => (
                            <ConditionSummary key={i} condition={c} tags={tags} />
                          ))}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            <Users className="w-3 h-3 mr-1" />
                            {segment.contactCount ?? 0} contatos
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {(segment.conditions ?? []).length} condição(ões)
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Editar" onClick={() => openEdit(segment)}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Duplicar" onClick={() => handleDuplicate(segment)}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                          title="Remover"
                          onClick={() => { if (confirm("Remover este segmento?")) deleteMutation.mutate({ id: segment.id }); }}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog de criação/edição */}
      <Dialog open={isOpen} onOpenChange={(o) => { if (!o) { setIsOpen(false); resetForm(); } }}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Segmento" : "Novo Segmento"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Nome e Descrição */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nome do segmento *</Label>
                <Input
                  className="mt-1"
                  value={formData.name}
                  onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ex: Secretários de Administração SP"
                />
              </div>
              <div className="col-span-2">
                <Label>Descrição (opcional)</Label>
                <Input
                  className="mt-1"
                  value={formData.description}
                  onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                  placeholder="Ex: Secretários de Adm. de prefeituras paulistas"
                />
              </div>
            </div>

            {/* Tipo de correspondência */}
            <div>
              <Label>Tipo de correspondência</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  variant={formData.matchType === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFormData(f => ({ ...f, matchType: "all" }))}
                >
                  Todas as condições (E)
                </Button>
                <Button
                  variant={formData.matchType === "any" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFormData(f => ({ ...f, matchType: "any" }))}
                >
                  Qualquer condição (OU)
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {formData.matchType === "all"
                  ? "Contatos que satisfazem TODAS as condições abaixo"
                  : "Contatos que satisfazem PELO MENOS UMA das condições abaixo"}
              </p>
            </div>

            <Separator />

            {/* Condições */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Condições do segmento</Label>
                <Badge variant="outline" className="text-xs">{formData.conditions.length} condição(ões)</Badge>
              </div>

              {formData.conditions.map((condition, idx) => (
                <div key={condition.id}>
                  {idx > 0 && (
                    <div className="flex items-center gap-2 my-2">
                      <div className="flex-1 border-t border-border/50" />
                      <Badge variant={formData.matchType === "all" ? "default" : "secondary"} className="text-xs">
                        {formData.matchType === "all" ? "E" : "OU"}
                      </Badge>
                      <div className="flex-1 border-t border-border/50" />
                    </div>
                  )}
                  <ConditionRow
                    condition={condition}
                    tags={tags}
                    onChange={(updates) => handleUpdateCondition(condition.id, updates)}
                    onRemove={() => handleRemoveCondition(condition.id)}
                    canRemove={formData.conditions.length > 1}
                  />
                </div>
              ))}

              <Button variant="outline" size="sm" onClick={handleAddCondition} className="w-full gap-2">
                <Plus className="w-3.5 h-3.5" />
                Adicionar condição
              </Button>
            </div>

            {/* Preview */}
            <div className="p-3 bg-muted/40 rounded-lg border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">
                    {previewCount !== null
                      ? `${previewCount} contato${previewCount !== 1 ? "s" : ""} encontrado${previewCount !== 1 ? "s" : ""}`
                      : "Clique para calcular quantos contatos este segmento atinge"}
                  </p>
                  <p className="text-xs text-muted-foreground">Preview em tempo real</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handlePreview} disabled={isPreviewLoading}>
                {isPreviewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
                {isPreviewLoading ? "Calculando..." : "Calcular"}
              </Button>
            </div>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => { setIsOpen(false); resetForm(); }}>Cancelar</Button>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending ? "Salvando..." : editingId ? "Atualizar Segmento" : "Criar Segmento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
    </PermissionGuard>
  );
}
